// SG-SITEWIDE-PERFORMANCE-RENDER-REPAIR-001-IMPL-004 — THE SHARED SCRIPT READINESS CONTRACT.
//
// THE MEASURED DEFECT, on the predecessor candidate 5f15d1c837ab870a8b0de25df2b6c9e6be74b53b.
// Two writers now insert the same three classic boot dependencies under the same `data-sg-design`
// marker, on purpose, so whichever wins the race the other reuses the tag instead of fetching twice:
//   · EarlyPaintReveal.tsx — a parser-first inline script, runs before any bundle;
//   · DesignSurface.tsx    — the React boot effect, runs only once hydration completes.
// EarlyPaintReveal was corrected (CORR-004) to treat a found tag as proof only of a STARTED fetch,
// and to read a completion marker or wait for the tag's own load/error. DesignSurface's own
// `loadScript` was NOT:
//
//     const existing = document.querySelector(`script[data-sg-design="${src}"]`);
//     if (existing) { resolve(); return; }          // ← pending? already 404'd? resolved either way
//
// On an ssr=true surface that is a false "up", not merely an early one. `paintVerdict(host)` is
// already "painted" at the first sample — react-dom/server put the real `[data-dc-tpl]` nodes in the
// response, hydration does not build them — so `await bootDependencies` is the ONLY thing between
// the effect and `window.__SG_DESIGN_STATE = "up"` + `host.style.visibility = "visible"` +
// `host.dataset.sgPaint = "painted"`. With EarlyPaintReveal's three tags on the page and the design
// system bundle still in flight (or already 404'd), that await resolved instantly and the boot
// published "up" over a design whose custom elements had never been defined. Every later check that
// gates on `designState === "up"` — the boot effect's own short-circuit, the restore predicate, the
// deployed gates — then read success. EarlyPaintReveal's identical hole is the one CORR-004 closed;
// this is the same hole in the other writer, and it is the one that writes the state.
//
// THE CONTRACT, ONE DEFINITION FOR BOTH WRITERS.
//   · `data-sg-design="<src>"`        — "a fetch for this src was STARTED". Presence proves nothing more.
//   · `data-sg-design-state="loaded"` — stamped by whichever writer created the tag, in its own
//                                        `onload`, i.e. only after the script actually executed.
//   · `data-sg-design-state="failed"` — stamped in `onerror`.
//   · no state attribute              — STILL PENDING. Events are dispatched synchronously, so a tag
//                                        whose creator stamps in its handler cannot have finished
//                                        without the marker being there to read.
// Readiness therefore comes from a proven completion marker or from the tag's OWN load/error event —
// never from the tag existing. Both writers stamp, so the contract is symmetric and there is no
// unmarked-but-finished tag either side can be fooled by.
//
// FAILS CLOSED. A failed dependency REJECTS, which lands in the boot effect's existing `.catch` →
// `__SG_DESIGN_STATE = "failed"` + `showServerFallback(host, true)`. A pending dependency simply does
// not settle until it does, which is the behaviour the original `loadScript` already had for a tag it
// had created itself (`s.onload` / `s.onerror`, no timeout) — so no new indefinite-wait class is
// introduced here, and on an ssr=true surface the server-rendered design is on screen from the first
// paint throughout (IMPL-003), with ServerLayerParserFirst's own ceiling still the outer bound.

/**
 * The three classic scripts the design template mounts against, in execution order (`_ds_bundle.js`
 * is a dependency of the two custom-element scripts). ONE list, shared by both writers:
 * DesignSurface.tsx's boot effect and EarlyPaintReveal.tsx's parser-first inline script. This module
 * is constants-plus-one-function with no "use client" and no React, so the server component can
 * import it without crossing the client boundary.
 */
export const BOOT_DEPENDENCIES = [
  "/design/_ds/snowgroup-design-system-6d9a4167-d513-4f87-84d5-03d3dc7c5420/_ds_bundle.js",
  "/design/image-slot.js",
  "/design/osm-map.js",
];

/**
 * IMPL-008 — THE #418 CAUSE, AND THE RULE THAT CLOSES IT.
 *
 * React 19 reported "Hydration failed because the server rendered HTML didn't match the client" on
 * every mobile mountain page whose server markup carries an `<osm-map>`. The development build's own
 * diff names it exactly: the client render expects `<osm-map data-dc-tpl>` to have NO children, and
 * the DOM React meets has two it never rendered — the loading-box poster and MapLibre's container.
 * Both are written into a React-owned element BEFORE hydration reaches it: the poster by
 * EarlyPaintReveal's parser-first script, the container by `osm-map.js` upgrading the element, which
 * the dependency loaders start the moment the page runs. Red/green, four ways, one runtime
 * configuration (hydration-418-repro.mjs --dev): both present → mismatch; poster only removed →
 * mismatch (the container); `osm-map.js` only failed → mismatch (the poster); BOTH removed → none.
 *
 * So no writer may add children to an `<osm-map>` until the design subtree that owns it has hydrated.
 * `osm-map.js` is the only boot dependency that does this to light-DOM children (`image-slot.js` uses
 * a shadow root; `_ds_bundle.js` defines no element that is rendered with server children), so it is
 * the only one held back — its `<script>` tag is not CREATED until hydration commits. The fetch still
 * starts early: all three scripts carry a `<link rel="preload">` (DesignSurface.tsx). The ssr host is
 * visible from first paint (IMPL-003), so holding the bookkeeping `up` for it delays no pixel ON THE
 * DESIGN HOST — but see the known limit below, which is about a DIFFERENT layer.
 *
 * ⚠ KNOWN LIMIT, MEASURED AND DELIBERATELY NOT CHANGED HERE (IMPL-008 self-review, 2026-10-01).
 * `osm-map.js` is one of the three entries EarlyPaintReveal's parser-first script requires before it
 * claims `__SG_DESIGN_STATE = "up"`, so holding it ALSO moves that parser-first "up" behind hydration —
 * on EVERY design route, including the 7 of 11 measured that serve no `<osm-map>` at all and therefore
 * have no #418 to protect. That matters because ServerLayerParserFirst UNCOVERS the server-fallback
 * layer (`.sg-export-head[data-sg-server-layer]`, which every design route serves exactly one of) when
 * the state is not "up" at load+2000ms, 12000ms or 20000ms. If hydration ever loses that race, a
 * duplicate heading block is revealed over an already-correct server render.
 *   MEASURED: it does not lose the race here. hydration-418-preserve-walk.mjs's FALLBACK leg holds the
 *   stamp at CPU 1x, 4x and 20x on /mountain-whistler/profile (state=up, revealedWhy=null, 0 layers
 *   shown), and that leg is red-proved — with the boot bundle 404'd it reports `stampHeld=false
 *   revealedWhy=stalled-boot`, so it can see a reveal.
 *   NOT MEASURED: network latency. This harness serves Next's handler in-process (the sandbox refuses
 *   every listen()), so a slow-transport run — the one condition under which hydration plausibly loses
 *   to load+2000ms — cannot be emulated here. Recorded as unproven, not as proven safe.
 *   ROUTED, NOT GUESSED: scoping the hold to documents that actually contain an `<osm-map>`, and/or
 *   taking the gated dependency out of the parser-first reveal's conjunction, would both remove this
 *   coupling — but both change the fail-closed semantics CORR-004 installed on purpose (a failed
 *   `osm-map.js` would then reach "up" instead of "failed"), and the 28-test focused suite pins the
 *   present behaviour. That is a semantics ruling, not a bug fix, so it is not taken unilaterally.
 *
 *   RULED 2026-10-02 (this packet's independent diagnosis, run fable-direct-20261002-070635): the
 *   coupling is closed by NEITHER of those two routes. The conjunction keeps every dependency and
 *   the hold stays on every design route; instead EarlyPaintReveal publishes
 *   EARLY_BOOT_LIVENESS_PROP (below) before it waits, and ServerLayerParserFirst's no-boot reveals
 *   — which exist for a route chunk that never ran, not for a boot that is alive and held — read it
 *   as proof of life. The 20s stalled-boot reveal ignores the marker on purpose, so the bound on a
 *   boot that never finishes is unchanged. Red/green: early-boot-liveness.test.ts, both real inline
 *   scripts in one vm realm.
 */
export const HYDRATION_GATED_DEPENDENCIES = ["/design/osm-map.js"];

/** Stamped on the design host when DesignBundle's mount effect has run, i.e. hydration has committed. */
export const DESIGN_HYDRATED_ATTR = "data-sg-hydrated";

/**
 * IMPL-008 (run 2026-10-02) — THE PARSER-FIRST LIVENESS MARKER, the ruling the KNOWN LIMIT above
 * asked for. The window property EarlyPaintReveal's inline script sets the moment it has decided it
 * OWNS a reveal attempt (host present, SSR-painted) and BEFORE it waits on anything — hydration,
 * fonts, the three boot dependencies. It answers exactly one question, ServerLayerParserFirst's
 * "did any script ever run?" (the load+grace and hard-ceiling no-boot reveals), so a healthy boot
 * whose `up` is lawfully held behind hydration is no longer misread as a dead route chunk at
 * load+2000ms and the legacy fallback no longer paints over an already-visible SSR design.
 *
 * DELIBERATELY NOT `__SG_DESIGN_STATE`. The state is a VERDICT ("booting"/"up"/"failed") owned by
 * its two writers and gated on the full dependency conjunction (CORR-004); liveness is not a
 * verdict, so it must not suppress the 20s stalled-boot reveal — `stalled()` keeps reading ONLY the
 * state, and an alive-but-never-up boot still uncovers the fallback at SERVER_LAYER_STALL_MS. A
 * failed dependency likewise still never reaches "up" (nothing about the conjunction changed).
 */
export const EARLY_BOOT_LIVENESS_PROP = "__SG_EARLY_BOOT";

/** Landing world map only; loaded after the mount by DesignSurface, never awaited by the boot. */
export const LANDING_MAP_DEPENDENCIES = [
  "/design/vendor/d3.min.js",
  "/design/vendor/topojson-client.min.js",
  "/design/vendor/world-110m.js",
];

/** "A fetch for this src was started." The value is the src, so the tag is findable by src. */
export const DESIGN_SCRIPT_SRC_ATTR = "data-sg-design";
/** The ONLY proof of completion. Absent = pending; see the header. */
export const DESIGN_SCRIPT_STATE_ATTR = "data-sg-design-state";
export const DESIGN_SCRIPT_LOADED = "loaded";
export const DESIGN_SCRIPT_FAILED = "failed";

/** What a found tag actually proves. */
export type DesignScriptReadiness = "loaded" | "failed" | "pending";

/** Just enough of the DOM to be driven without a browser (the idiom EarlyPaintReveal.test.ts uses). */
/** Just enough of a <script> tag for this contract. Structural on purpose: HTMLScriptElement satisfies
 *  it, and so does a hand-rolled stand-in, which is what makes the rule provable in a node test. */
export interface DesignScriptTag {
  src: string;
  async: boolean;
  onload: ((...a: never[]) => void) | null;
  onerror: ((...a: never[]) => void) | null;
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  addEventListener(type: string, listener: () => void): void;
}

/** Just enough of the document, same reason. `appendChild` is typed loosely because the real
 *  `HTMLHeadElement.appendChild` is `<T extends Node>(node: T) => T`, and a structural tag is not a
 *  Node — insisting on Node here would make the contract untestable without a browser. */
export interface DesignScriptDoc {
  querySelector(selectors: string): Pick<DesignScriptTag, "getAttribute" | "addEventListener"> | null;
  createElement(tag: "script"): DesignScriptTag;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  head: { appendChild(el: any): unknown };
}

/** Byte-for-byte the selector EarlyPaintReveal.tsx's inline script builds, so both writers look for
 *  the same tag. The sources are this module's own hardcoded paths — no quoting to escape. */
export function designScriptSelector(src: string): string {
  return `script[${DESIGN_SCRIPT_SRC_ATTR}="${src}"]`;
}

/**
 * Grade an existing tag. Anything that is not an explicit success or an explicit failure marker is
 * PENDING — including a marker with an unrecognised value, which is a tag written by something that
 * does not speak this contract and must not be read as success.
 */
export function readinessOf(el: { getAttribute(name: string): string | null } | null): DesignScriptReadiness {
  if (!el) return "pending";
  const state = el.getAttribute(DESIGN_SCRIPT_STATE_ATTR);
  if (state === DESIGN_SCRIPT_LOADED) return "loaded";
  if (state === DESIGN_SCRIPT_FAILED) return "failed";
  return "pending";
}

export function designDependencyError(src: string): Error {
  return new Error(`design dependency failed to load: ${src}`);
}

/**
 * Load one classic boot dependency, or adopt the tag another writer already started.
 *
 * Resolves ONLY on proven execution: our own `loaded` marker, or this tag's own `load` event.
 * Rejects on the `failed` marker or an `error` event. Never resolves merely because a tag exists.
 */
export function loadDesignScript(src: string, doc: DesignScriptDoc = document): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const existing = doc.querySelector(designScriptSelector(src));
    if (existing) {
      const readiness = readinessOf(existing);
      if (readiness === "loaded") { resolve(); return; }
      if (readiness === "failed") { reject(designDependencyError(src)); return; }
      // Pending: the tag's own completion event is the proof. Its creator stamps the marker in the
      // same handler, so attaching here cannot miss a completion that already happened.
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(designDependencyError(src)));
      return;
    }
    const s = doc.createElement("script");
    s.src = src;
    s.async = false; // executed in list order
    s.setAttribute(DESIGN_SCRIPT_SRC_ATTR, src);
    s.onload = () => {
      // Stamp BEFORE resolving: the other writer may be waiting on this marker.
      s.setAttribute(DESIGN_SCRIPT_STATE_ATTR, DESIGN_SCRIPT_LOADED);
      resolve();
    };
    s.onerror = () => {
      s.setAttribute(DESIGN_SCRIPT_STATE_ATTR, DESIGN_SCRIPT_FAILED);
      reject(designDependencyError(src));
    };
    doc.head.appendChild(s);
  });
}

/**
 * THE BOOT'S DEPENDENCY GATE — the single expression DesignSurface.tsx's boot effect awaits.
 *
 * Nothing downstream of it can run until it resolves: the paint poll, `__SG_DESIGN_STATE = "up"`,
 * `host.style.visibility = "visible"` and `host.dataset.sgPaint = "painted"` are all after the
 * `await`. A rejection lands in that effect's own `.catch`, which writes state="failed" and calls
 * `showServerFallback(host, true)`. So "a failed or pending dependency never marks the design up" is
 * a property OF THIS FUNCTION, and provable here without a React tree.
 *
 * `hydrated` holds back the dependencies in HYDRATION_GATED_DEPENDENCIES until it resolves (their tag is
 * not created before). It does not weaken the gate: a gated dependency that never loads, or whose
 * `hydrated` never resolves, still keeps this promise from resolving, so nothing downstream runs.
 *
 * `load`, `doc` are injectable for tests only; production passes only `hydrated`, and only on an
 * ssr host (a client-rendered host has no hydration to protect, so it passes nothing and loads at once).
 */
export function bootDependenciesReady(
  load: (src: string, doc?: DesignScriptDoc) => Promise<void> = loadDesignScript,
  doc?: DesignScriptDoc,
  hydrated: Promise<void> = Promise.resolve(),
): Promise<void> {
  return Promise.all(
    BOOT_DEPENDENCIES.map((src) =>
      HYDRATION_GATED_DEPENDENCIES.includes(src) ? hydrated.then(() => load(src, doc)) : load(src, doc),
    ),
  ).then(() => undefined);
}
